import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Crosshair } from 'lucide-react';
import { getSearchChannels, searchPoint } from '../../services/api';
import {
  convertValue, getDisplayUnit, convertDistanceFt, getDistanceUnit,
} from '../../utils/unitConvert';
import styles from './SearchPage.module.css';

const CATEGORY_ORDER = ['Dynamics', 'Engine', 'Driver inputs', 'Other'];

function fmtDate(s) {
  if (!s) return '—';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString(undefined, { year: '2-digit', month: 'short', day: 'numeric' });
}

function fmtVal(v) {
  if (v == null || Number.isNaN(v)) return '—';
  const abs = Math.abs(v);
  const digits = abs >= 100 ? 0 : abs >= 10 ? 1 : 2;
  return v.toFixed(digits);
}

/**
 * Point query — rank in-scope laps by a channel's value at a picked spot on
 * the lap (fraction of lap distance, set by clicking the trace or map).
 */
export default function PointQueryPanel({
  trackId, scope, vehicleId, pointFrac, lapTotalFt, unitPrefs, onLoadSession,
  initialState, onStateChange,
}) {
  const [channels, setChannels] = useState([]);
  const [channelKey, setChannelKey] = useState(initialState?.channelKey || 'speed');
  const [agg, setAgg] = useState(initialState?.agg || 'max');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stale, setStale] = useState(false);
  const [error, setError] = useState(null);
  // Whether a search has completed since the last invalidation — reported up
  // so a Back-navigation/deep-link restore knows to auto re-run it (below).
  const [ran, setRan] = useState(false);

  // ── Channels available at this track/scope/car ───────────────────────────
  useEffect(() => {
    if (!trackId) { setChannels([]); return; }
    let cancelled = false;
    getSearchChannels(trackId, scope, vehicleId).then(res => {
      if (cancelled) return;
      const chs = res.channels || [];
      setChannels(chs);
      setChannelKey(prev =>
        (chs.some(c => c.key === prev) ? prev
          : (chs.find(c => c.key === 'speed')?.key || chs[0]?.key || null)));
    }).catch(() => !cancelled && setChannels([]));
    return () => { cancelled = true; };
  }, [trackId, scope, vehicleId]);

  // New track/scope/car invalidates what's on screen — except the very first
  // time, when a restore may still need its auto-run (below) to see a
  // `ran: true` it hasn't consumed yet.
  const firstInvalidateRef = useRef(true);
  useEffect(() => {
    if (firstInvalidateRef.current) firstInvalidateRef.current = false;
    else setRan(false);
    setResult(null); setStale(false); setError(null);
  }, [trackId, scope, vehicleId]);
  useEffect(() => { if (result) setStale(true); }, [pointFrac, channelKey, agg]); // eslint-disable-line react-hooks/exhaustive-deps

  const grouped = useMemo(() => {
    const by = {};
    for (const c of channels) (by[c.category] ||= []).push(c);
    return CATEGORY_ORDER.filter(cat => by[cat]).map(cat => [cat, by[cat]]);
  }, [channels]);

  const channel = channels.find(c => c.key === channelKey);

  const run = useCallback(async () => {
    if (!trackId || !channelKey || pointFrac == null) return;
    setLoading(true);
    setError(null);
    try {
      const res = await searchPoint({
        track_id: trackId, scope, channel_key: channelKey,
        vehicle_id: vehicleId, frac: pointFrac, agg, top_n: 10,
      });
      setResult(res);
      setStale(false);
      setRan(true);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setLoading(false);
    }
  }, [trackId, scope, vehicleId, channelKey, pointFrac, agg]);

  // Auto re-run once, only when restoring a page where a search had already
  // completed (initialState.ran) — waits for the point + channel to be ready.
  const autoRunRef = useRef(!!initialState?.ran);
  useEffect(() => {
    if (!autoRunRef.current || !trackId || !channelKey || pointFrac == null) return;
    autoRunRef.current = false;
    run();
  }, [trackId, channelKey, pointFrac, run]);

  useEffect(() => { onStateChange?.({ channelKey, agg, ran }); }, [channelKey, agg, ran, onStateChange]);

  const distLabel = useMemo(() => {
    if (pointFrac == null || !lapTotalFt) return null;
    const d = convertDistanceFt(pointFrac * lapTotalFt, unitPrefs);
    return `${Math.round(d).toLocaleString()} ${getDistanceUnit(unitPrefs)} (${Math.round(pointFrac * 100)}% of lap)`;
  }, [pointFrac, lapTotalFt, unitPrefs]);

  const dispUnit = result?.unit ? getDisplayUnit(result.unit, unitPrefs) : (channel?.unit || '');

  return (
    <section className={styles.card}>
      <div className={styles.cardHead}>
        <span><Crosshair size={14} /> Channel value at a point</span>
        <span className={styles.headActions}>
          {stale && <span className={styles.dirtyBadge}>inputs changed — re-run</span>}
          <button className={styles.runBtn} onClick={run}
            disabled={loading || pointFrac == null || !channelKey}>
            {loading ? 'Searching…' : 'Run point search'}
          </button>
        </span>
      </div>

      <div className={styles.pointControls}>
        <div className={styles.ctl}>
          <span>Point</span>
          <div className={styles.pointReadout}>
            {distLabel || 'Click the speed trace or track map to pick a point'}
          </div>
        </div>
        <label className={styles.ctl}>
          <span>Channel</span>
          <select value={channelKey || ''} onChange={e => setChannelKey(e.target.value)}>
            {grouped.map(([cat, chs]) => (
              <optgroup key={cat} label={cat}>
                {chs.map(c => (
                  <option key={c.key} value={c.key}>
                    {c.label}{c.unit ? ` (${c.unit})` : ''} — {Math.round((c.coverage || 0) * 100)}%
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </label>
        <div className={styles.ctl}>
          <span>Rank by</span>
          <div className={styles.segmented}>
            <button className={agg === 'max' ? styles.segOn : styles.seg}
              onClick={() => setAgg('max')}>Highest</button>
            <button className={agg === 'min' ? styles.segOn : styles.seg}
              onClick={() => setAgg('min')}>Lowest</button>
          </div>
        </div>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {result?.mixed_units && (
        <div className={styles.warn}>
          Sessions log this channel in different units — rankings may mix scales.
        </div>
      )}

      {!result && !error && (
        <div className={styles.empty}>
          Pick a point, choose a channel, then run to rank laps by their value there.
        </div>
      )}

      {result && (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>#</th><th>Driver</th><th>Date</th>
              <th>{channel?.label || result.channel_key}{dispUnit ? ` (${dispUnit})` : ''}</th>
              <th>Lap</th><th>Vehicle</th>
            </tr>
          </thead>
          <tbody>
            {result.results.map((r, i) => (
              <tr key={`${r.session_id}-${r.lap_number}`} className={styles.linkRow}
                title="Open this session in the analysis view"
                onClick={() => onLoadSession?.(r.session_id)}>
                <td className={styles.muted}>{i + 1}</td>
                <td>{r.user_display_name || '—'}</td>
                <td>{fmtDate(r.event_date)}</td>
                <td><strong>{fmtVal(convertValue(r.value, r.unit || '', unitPrefs))}</strong></td>
                <td title={`Lap ${r.lap_number}`}>{r.lap_time_str}</td>
                <td>{r.vehicle_name || '—'}</td>
              </tr>
            ))}
            {result.results.length === 0 && (
              <tr><td colSpan={6} className={styles.empty}>No laps logged this channel here.</td></tr>
            )}
          </tbody>
        </table>
      )}
    </section>
  );
}
